import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { CalendarDaysIcon, ChevronRightIcon, ClipboardCheckIcon, FileSearchIcon, FlaskConicalIcon } from 'lucide-react';
import { abbVieProBranches, chapterProgressionModel } from '../data/abbviePro';
const iconMap = {
  clinical: FlaskConicalIcon,
  safety: ClipboardCheckIcon,
  dosing: FileSearchIcon,
  resources: CalendarDaysIcon
};
export function AbbVieBranchingScreen() {
  const reduceMotion = useReducedMotion();
  return <section className="flex min-h-screen items-center bg-presentation-canvas px-6 py-20" aria-labelledby="branching-title" data-id="element-17" data-mp="3rxhj6c4q77ip">
      <div className="mx-auto w-full max-w-7xl" data-id="element-18" data-mp="3e6m94wihqkdj">
        <div className="mx-auto mb-12 max-w-3xl text-center" data-id="element-19" data-mp="3j4nnx58vvxj7">
          <p className="mb-4 text-sm font-semibold text-primary" data-id="element-20" data-mp="3j55rkmxvle15">06 - HCP website architecture</p>
          <h2 id="branching-title" className="text-4xl font-bold leading-tight text-secondary md:text-6xl" data-id="element-21" data-mp="347wzfr0r068d">Add a pathway, don't rebuild the library</h2>
          <p className="mt-5 text-lg text-slate-500" data-id="element-22" data-mp="27fw26nltx63w">The existing structure stays intact. A visual player entry point and digestible episodic pathway sit alongside the traditional text library.</p>
        </div>

        <div className="mx-auto mb-8 grid max-w-5xl gap-3 sm:grid-cols-3" data-id="element-23" data-mp="2ghc9unl9eu4f">
          {chapterProgressionModel.map((step, index) => <motion.div key={step.chapter} initial={reduceMotion ? false : {
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.22,
          delay: index * 0.05,
          ease: [0.23, 1, 0.32, 1]
        }} className="rounded-xl border border-slate-200 bg-white p-4" data-id="element-24" data-mp="2cfqr4lm1nlmb" data-mpi={index}>
              <p className="text-xs font-bold text-abbvie-navy" data-id="element-25" data-mp="1spotv0pjd3gx">{step.chapter}</p>
              <div className="mt-3 flex h-2 overflow-hidden rounded-full bg-slate-100" data-id="element-26" data-mp="150m3n4gu62at">
                <div className="h-full bg-abbvie-navy" style={{
              width: `${step.sciencePercent}%`
            }} data-id="element-27" data-mp="110vet6avdw8x" />
                <div className="h-full bg-abbvie-cyan" style={{
              width: `${step.brandPercent}%`
            }} data-id="element-28" data-mp="305ln194nbn4c" />
              </div>
              <p className="mt-3 text-xs leading-relaxed text-slate-500" data-id="element-29" data-mp="352lu9nd8evx1">{step.description}</p>
            </motion.div>)}
        </div>
        <p className="mx-auto mb-14 max-w-2xl text-center text-xs font-semibold text-slate-400" data-id="element-30" data-mp="3etqa0paqeksx">
          Science-to-brand mix adapts by region and audience maturity.
        </p>

        <div className="mx-auto max-w-6xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl" data-id="element-31" data-mp="2h82gqd9koilm">
          <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4 sm:px-8" data-id="element-32" data-mp="2r45el6xe5v2r">
            <div className="flex items-center gap-2 text-abbvie-navy" data-id="element-33" data-mp="2w2yuf605hy63">
              <span className="text-xl font-semibold tracking-tight" data-id="element-34" data-mp="2w15mzohpr9x1">abbvie</span>
              <span className="rounded bg-abbvie-pro px-2 py-0.5 text-[10px] font-bold text-white" data-id="element-35" data-mp="6denim3xrgqr">pro</span>
            </div>
            <span className="text-xs font-semibold text-abbvie-cyan" data-id="element-36" data-mp="beucuqd5c9ey">SKYRIZI - GASTROENTEROLOGY</span>
          </div>

          <div className="bg-sky-50 px-5 py-8 text-center sm:px-8 sm:py-10" data-id="element-37" data-mp="1qw7dg59pz3em">
            <p className="text-xs font-bold text-abbvie-magenta" data-id="element-38" data-mp="1qybfeho25sc9">CONTINUE YOUR SKYRIZI JOURNEY</p>
            <h3 className="mt-2 text-2xl font-bold text-abbvie-navy sm:text-3xl" data-id="element-39" data-mp="1m2w3hv9m26ih">What would be most useful right now?</h3>
          </div>

          <div className="grid gap-px bg-slate-200 sm:grid-cols-2 lg:grid-cols-4" data-id="element-40" data-mp="1c7b7qev4y2h6">
            {abbVieProBranches.map((branch, index) => {
            const Icon = iconMap[branch.icon];
            return <motion.button key={branch.title} type="button" initial={reduceMotion ? false : {
              opacity: 0,
              y: 12
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.22,
              delay: index * 0.035,
              ease: [0.23, 1, 0.32, 1]
            }} className="group flex min-h-56 flex-col bg-white p-6 text-left transition-[background-color] duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] hover:bg-sky-50 focus:z-10 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-abbvie-cyan" data-id="element-41" data-mp="137fqbsazlb8p" data-mpi={index}>
                  <Icon className="h-8 w-8 text-abbvie-cyan" aria-hidden="true" data-id="element-42" data-mp="1gc35min7pxss" />
                  <h4 className="mt-8 text-lg font-bold text-abbvie-navy" data-id="element-43" data-mp="1l6z1jtvkl5b6">{branch.title}</h4>
                  <p className="mt-2 text-sm leading-relaxed text-slate-500" data-id="element-44" data-mp="16imczhytaoqp">{branch.description}</p>
                  <span className="mt-auto flex items-center gap-1 pt-6 text-xs font-bold text-abbvie-magenta" data-id="element-45" data-mp="1bbx9oq9wtp4w">
                    Explore <ChevronRightIcon className="h-4 w-4 transition-transform duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] group-hover:translate-x-1" data-id="element-46" data-mp="xhegn5fuvxz9" />
                  </span>
                </motion.button>;
          })}
          </div>
        </div>
      </div>
    </section>;
}